import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { secureUpload } from '../../services/s3UploadService';
import { useAuth } from '../../context/AuthContext';

const { FiDollarSign, FiCalendar, FiImage, FiFileText, FiUploadCloud, FiLoader } = FiIcons;

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full px-4 py-3.5 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-medium transition-colors";

const CampaignBasicFields = ({ form, update }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const { s3Url } = await secureUpload({
        file,
        ownerId: user?.id || 'guest',
        category: 'campaign_covers',
        onProgress: (p) => setUploadProgress(p)
      });
      update('cover_image_url', s3Url);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(err.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Field label="Campaign Title *">
        <input type="text" value={form.title} onChange={e => update('title', e.target.value)}
          placeholder="e.g. In Memory of Jane Doe — Cancer Research Fund"
          className={inputCls} />
      </Field>

      <Field label="Description *">
        <div className="relative">
          <SafeIcon icon={FiFileText} className="absolute left-4 top-4 w-4 h-4 text-text-muted" />
          <textarea value={form.description} onChange={e => update('description', e.target.value)}
            rows={4} placeholder="Describe the campaign and its impact…"
            className={`${inputCls} pl-11 resize-none`} />
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Fundraising Goal (USD) *">
          <div className="relative">
            <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input type="number" value={form.goal_amount} onChange={e => update('goal_amount', e.target.value)}
              placeholder="5000" min="1" className={`${inputCls} pl-10`} />
          </div>
        </Field>
        <Field label="Campaign Deadline *">
          <div className="relative">
            <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input type="date" value={form.deadline} onChange={e => update('deadline', e.target.value)}
              min={minDateStr} className={`${inputCls} pl-10`} />
          </div>
        </Field>
      </div>

      <Field label="Cover Image URL (optional)">
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SafeIcon icon={FiImage} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input type="url" value={form.cover_image_url} onChange={e => update('cover_image_url', e.target.value)}
                placeholder="https://… or upload a file" className={`${inputCls} pl-10`} disabled={uploading} />
            </div>
            <div className="relative w-32 flex-shrink-0">
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10" />
              <div className={`absolute inset-0 flex items-center justify-center gap-2 rounded-2xl border-2 transition-all ${uploading ? 'bg-brand-light border-brand-dark text-text-muted' : 'bg-accent-navy border-accent-navy text-white hover:bg-accent-navy/90'}`}>
                {uploading ? (
                  <><SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /><span className="text-xs font-bold">{uploadProgress}%</span></>
                ) : (
                  <><SafeIcon icon={FiUploadCloud} className="w-4 h-4" /><span className="text-xs font-bold">Upload</span></>
                )}
              </div>
            </div>
          </div>
          {form.cover_image_url && (
            <img src={form.cover_image_url} alt="Preview" onError={e => e.target.style.display = 'none'}
              className="h-32 w-full object-cover rounded-2xl border border-brand-dark" />
          )}
        </div>
      </Field>
    </div>
  );
};

export default CampaignBasicFields;